/**
 * CRM scoreboard (A7Y-4981): every account from `GET /admin/crm/accounts` as a
 * sortable, filterable table, with the customer and partner stage counts and
 * the active conversation slots above it.
 *
 * The page loads the three CRM reads once and sorts, filters and searches the
 * ~100 account rows in the browser (`scoreboard.ts`). Stage counts come from
 * the API (`customerByStage` / `partnerByStage`) and are never computed here.
 * Each row opens its account page (A7Y-4982) and carries the stage, next action
 * and last-touch editors; the page adds accounts (A7Y-4983) and links to the
 * this-week view (A7Y-4984) and the pipeline board (A7Y-5209).
 *
 * States: outside production the API answers 409 `CRM_UNAVAILABLE`, shown as a
 * plain notice (not an error); before the cutover import the reads answer
 * `imported: false`, shown in place of the table.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CRM_PRIORITIES, CRM_SEGMENTS, CRM_STAGES, CRM_STALE_AFTER_DAYS } from '@a7y/types';
import type { CrmAccountListItem, CrmStage } from '@a7y/types';
import { Badge } from '@a7y/tailwind-catalyst';
import { PageHeader } from '@/components/PageHeader';
import { HelmViewNav } from '../HelmViews';
import { SlotsPanel } from './SlotsPanel';
import { helmOneLine, helmPlainText } from '@a7y/platform-health/helm';
import { AccountNameLink, InlineMarkdown } from './CrmMarkdown';
import { useIsMobile } from '@/capital/useIsMobile';
import { api } from '@/lib/api';
import {
  AddAccountForm,
  CONTROL_CLASSES,
  LINK_BUTTON,
  LastTouchEditor,
  NextActionEditor,
  StageEditor,
  Toggle,
} from './CrmEditors';
import { isCrmUnavailableError } from './crmErrors';
import {
  DEFAULT_SORT,
  NO_FILTERS,
  STAGE_ORDER,
  filterAccounts,
  nextActionText,
  nextSort,
  rankLabel,
  sortAccounts,
} from './scoreboard';
import type { Filters, KindFilter, RankFilter, SortKey, SortState } from './scoreboard';

/** A next action longer than this shows its first characters until expanded. */
export const NEXT_ACTION_PREVIEW_CHARS = 140;

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'account', label: 'Account' },
  { key: 'rank', label: 'Rank' },
  { key: 'contact', label: 'Primary contact' },
  { key: 'stage', label: 'Stage' },
  { key: 'segment', label: 'Segment' },
  { key: 'lastTouch', label: 'Last touch' },
  { key: 'nextAction', label: 'Next action' },
  { key: 'nextDate', label: 'Next date' },
];

function crmRetry(err: unknown, failureCount: number): boolean {
  // A 409 is the environment's answer, not a fault: never retry it.
  return !isCrmUnavailableError(err) && failureCount < 1;
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function CrmPage() {
  const summaryQuery = useQuery({
    queryKey: ['crm', 'summary'],
    queryFn: api.getCrmSummary,
    retry: crmRetry,
  });
  const accountsQuery = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: api.getCrmAccounts,
    retry: crmRetry,
  });
  const slotsQuery = useQuery({
    queryKey: ['crm', 'slots'],
    queryFn: api.getCrmSlots,
    retry: crmRetry,
  });
  const queries = [summaryQuery, accountsQuery, slotsQuery];
  const failure = queries.map((q) => q.error).find((e) => e != null);

  let body: React.ReactNode;
  if (queries.some((q) => isCrmUnavailableError(q.error))) {
    body = (
      <p data-testid="crm-unavailable" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record is available on production only.
      </p>
    );
  } else if (failure != null) {
    body = (
      <p
        role="alert"
        data-testid="crm-error"
        className="px-2 text-sm text-red-600 dark:text-red-400"
      >
        Could not load the accounts: {describeError(failure)}
      </p>
    );
  } else if (queries.some((q) => q.data === undefined)) {
    body = (
      <p data-testid="crm-loading" className="px-2 text-sm text-zinc-500">
        Loading…
      </p>
    );
  } else if (
    !summaryQuery.data!.imported ||
    !accountsQuery.data!.imported ||
    !slotsQuery.data!.imported
  ) {
    body = (
      <p data-testid="crm-not-imported" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record has not been imported yet
      </p>
    );
  } else {
    const accounts = accountsQuery.data!.accounts;
    const counts = summaryQuery.data!.counts;
    body = (
      <div className="space-y-8">
        <StageCounts
          customerByStage={counts.customerByStage}
          partnerByStage={counts.partnerByStage}
        />
        <SlotsPanel
          slots={slotsQuery.data!.slots}
          parked={slotsQuery.data!.parked}
          accounts={accounts}
        />
        <Scoreboard accounts={accounts} />
      </div>
    );
  }

  return (
    <div className="pb-12">
      <HelmViewNav />
      <PageHeader title="Accounts" subtitle="Accounts, stages and next actions" />
      <nav aria-label="Account actions" className="flex flex-wrap items-start gap-4 px-2 pb-4">
        <Toggle label="Add account">{(close) => <AddAccountForm onClose={close} />}</Toggle>
      </nav>
      {body}
    </div>
  );
}

function StageCounts({
  customerByStage,
  partnerByStage,
}: {
  customerByStage: Record<CrmStage, number>;
  partnerByStage: Record<CrmStage, number>;
}) {
  const rows = [
    { id: 'customer', label: 'Customers', counts: customerByStage },
    { id: 'partner', label: 'Partners', counts: partnerByStage },
  ];
  return (
    <section aria-label="Counts by stage" className="px-2">
      <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">Counts by stage</h2>
      <div className="overflow-x-auto">
        <table className="text-sm" data-testid="crm-counts">
          <thead>
            <tr className="text-left text-xs text-zinc-500 dark:text-zinc-400">
              <th className="pr-4 font-medium" />
              {CRM_STAGES.map((stage) => (
                <th key={stage} className="px-3 text-right font-medium capitalize">
                  {stage}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="text-zinc-900 dark:text-zinc-100">
                <th scope="row" className="pr-4 text-left font-medium">
                  {row.label}
                </th>
                {CRM_STAGES.map((stage) => (
                  <td
                    key={stage}
                    data-testid={`crm-count-${row.id}-${stage}`}
                    className="px-3 text-right tabular-nums"
                  >
                    {row.counts[stage]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Scoreboard({ accounts }: { accounts: CrmAccountListItem[] }) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const isMobile = useIsMobile();
  const rows = sortAccounts(filterAccounts(accounts, filters), sort);

  return (
    <section aria-label="Accounts" className="px-2">
      <div className="mb-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Search
          <input
            type="search"
            value={filters.search}
            placeholder="Account or contact"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className={CONTROL_CLASSES}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Rank
          <select
            value={filters.rank}
            onChange={(e) => setFilters({ ...filters, rank: e.target.value as RankFilter })}
            className={CONTROL_CLASSES}
            data-testid="crm-filter-rank"
          >
            <option value="any">Any rank</option>
            {CRM_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {rankLabel(priority)}
              </option>
            ))}
            <option value="unranked">Not ranked</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Stage
          <select
            value={filters.stage}
            onChange={(e) => setFilters({ ...filters, stage: e.target.value as Filters['stage'] })}
            className={CONTROL_CLASSES}
          >
            <option value="any">Any stage</option>
            {STAGE_ORDER.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Segment
          <select
            value={filters.segment}
            onChange={(e) =>
              setFilters({ ...filters, segment: e.target.value as Filters['segment'] })
            }
            className={CONTROL_CLASSES}
          >
            <option value="any">Any segment</option>
            {CRM_SEGMENTS.map((segment) => (
              <option key={segment} value={segment}>
                {segment}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Customer or partner
          <select
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value as KindFilter })}
            className={CONTROL_CLASSES}
          >
            <option value="any">Customers and partners</option>
            <option value="customer">Customers</option>
            <option value="partner">Partners</option>
          </select>
        </label>
        <label
          className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300"
          title={`Stale, the same rule as Today: a roster item more than ${CRM_STALE_AFTER_DAYS} days late, or nothing owed and no touch in ${CRM_STALE_AFTER_DAYS} days`}
        >
          <input
            type="checkbox"
            checked={filters.staleOnly}
            onChange={(e) => setFilters({ ...filters, staleOnly: e.target.checked })}
          />
          Stale only
        </label>
        <span data-testid="crm-row-count" className="pb-2 text-xs text-zinc-500">
          {rows.length} of {accounts.length} accounts
        </span>
      </div>

      {isMobile ? (
        <AccountCards rows={rows} sort={sort} onSort={setSort} />
      ) : (
      <div className="max-h-[70vh] overflow-auto rounded-md border border-zinc-200 dark:border-zinc-700">
        <table data-testid="crm-table" className="w-full min-w-[60rem] text-left text-sm">
          <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={
                    sort.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                  className="px-3 py-2 font-medium text-zinc-700 dark:text-zinc-300"
                >
                  <button
                    type="button"
                    onClick={() => setSort(nextSort(sort, col.key))}
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    {col.label}
                    <span aria-hidden="true" className="text-xs text-zinc-400">
                      {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  data-testid="crm-empty"
                  className="px-3 py-6 text-center text-zinc-500"
                >
                  No accounts match these filters.
                </td>
              </tr>
            ) : (
              rows.map((item) => <AccountRow key={item.account.id} item={item} />)
            )}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}

function AccountRow({ item }: { item: CrmAccountListItem }) {
  const { account, primaryContact } = item;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const action = nextActionText(item);
  // A7Y-5342: the collapsed preview is plain text cut on a word; the full text renders its
  // marks and links — never raw `**bold**` or `[note](…)`.
  const long = action !== null && helmPlainText(action).length > NEXT_ACTION_PREVIEW_CHARS;
  const shownAction: React.ReactNode =
    action === null
      ? '—'
      : long && !expanded
        ? helmOneLine(action, NEXT_ACTION_PREVIEW_CHARS)
        : <InlineMarkdown text={action} />;

  return (
    <Fragment>
    <tr data-testid="crm-row" className="align-top text-zinc-900 dark:text-zinc-100">
      <td className="px-3 py-2 font-medium" data-testid="crm-cell-account">
        <AccountNameLink accountId={account.id} name={account.name} className="hover:underline" />
        {item.stale ? (
          <Badge color="amber" className="ml-2" data-testid="crm-stale-badge">
            Stale
          </Badge>
        ) : null}
        <button
          type="button"
          aria-expanded={editing}
          onClick={() => setEditing(!editing)}
          className={`${LINK_BUTTON} block font-normal`}
        >
          {editing ? 'Done editing' : 'Edit'}
        </button>
      </td>
      <td className="px-3 py-2 whitespace-nowrap" data-testid="crm-cell-rank">
        {rankLabel(account.priority)}
      </td>
      <td className="px-3 py-2" data-testid="crm-cell-contact">
        {primaryContact === null ? (
          '—'
        ) : primaryContact.departed ? (
          <span className="text-zinc-500 line-through" title="Departed">
            {primaryContact.name}
            <span className="sr-only"> (departed)</span>
          </span>
        ) : (
          primaryContact.name
        )}
      </td>
      <td className="px-3 py-2" data-testid="crm-cell-stage">
        <span className="capitalize">{account.stage}</span>
        {account.stageNote ? (
          <span className="block text-xs text-zinc-500">
            <InlineMarkdown text={account.stageNote} />
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 capitalize" data-testid="crm-cell-segment">
        {account.segment}
      </td>
      <td className="px-3 py-2 whitespace-nowrap" data-testid="crm-cell-last-touch">
        {account.lastTouch ?? '—'}
      </td>
      <td className="max-w-xl px-3 py-2" data-testid="crm-cell-next-action">
        <span className="whitespace-pre-wrap break-words">{shownAction}</span>
        {long ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            className="ml-2 text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        ) : null}
      </td>
      <td className="px-3 py-2 whitespace-nowrap" data-testid="crm-cell-next-date">
        {item.nextDate ?? '—'}
      </td>
    </tr>
    {editing ? (
      <tr data-testid="crm-row-editors">
        <td colSpan={COLUMNS.length} className="bg-zinc-50/60 px-3 py-3 dark:bg-zinc-900/60">
          <AccountEditors account={account} />
        </td>
      </tr>
    ) : null}
    </Fragment>
  );
}

/** The editors a scoreboard row or card opens in place. */
function AccountEditors({ account }: { account: CrmAccountListItem['account'] }) {
  return (
    <div className="flex flex-wrap items-start gap-4">
      <Toggle label="Change stage">{(close) => <StageEditor account={account} onClose={close} />}</Toggle>
      <Toggle label="Edit next action">
        {(close) => <NextActionEditor account={account} onClose={close} />}
      </Toggle>
      <Toggle label="Set last touch">
        {(close) => <LastTouchEditor account={account} onClose={close} />}
      </Toggle>
    </div>
  );
}

/**
 * A7Y-5210: the Accounts list below 768 px (`useIsMobile`), where the
 * eight-column table cannot fit a phone: one card per account, with the
 * table's order chosen from a select instead of its column headers.
 */
function AccountCards({
  rows,
  sort,
  onSort,
}: {
  rows: CrmAccountListItem[];
  sort: SortState;
  onSort: (sort: SortState) => void;
}) {
  return (
    <>
      <div className="mb-2 flex items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Sort by
          <select
            data-testid="crm-sort-select"
            value={sort.key}
            onChange={(e) => onSort({ key: e.target.value as SortKey, dir: 'asc' })}
            className={CONTROL_CLASSES}
          >
            {COLUMNS.map((col) => (
              <option key={col.key} value={col.key}>
                {col.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          data-testid="crm-sort-direction"
          onClick={() => onSort({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
          className={LINK_BUTTON}
        >
          {sort.dir === 'asc' ? 'Ascending ▲' : 'Descending ▼'}
        </button>
      </div>
      {rows.length === 0 ? (
        <p data-testid="crm-empty" className="py-6 text-center text-sm text-zinc-500">
          No accounts match these filters.
        </p>
      ) : (
        <ul data-testid="crm-cards" className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {rows.map((item) => (
            <AccountCard key={item.account.id} item={item} />
          ))}
        </ul>
      )}
    </>
  );
}

function AccountCard({ item }: { item: CrmAccountListItem }) {
  const { account, primaryContact } = item;
  const [editing, setEditing] = useState(false);
  const action = nextActionText(item);
  const shownAction: React.ReactNode =
    action === null
      ? null
      : helmPlainText(action).length > NEXT_ACTION_PREVIEW_CHARS
        ? helmOneLine(action, NEXT_ACTION_PREVIEW_CHARS)
        : <InlineMarkdown text={action} />;
  return (
    <li data-testid="crm-card" className="space-y-1 py-3 text-sm text-zinc-900 dark:text-zinc-100">
      <div className="flex flex-wrap items-center gap-2">
        <AccountNameLink
          accountId={account.id}
          name={account.name}
          className="font-medium hover:underline"
          testId="crm-card-account"
        />
        <Badge color="zinc">{rankLabel(account.priority)}</Badge>
        {item.stale ? (
          <Badge color="amber" data-testid="crm-stale-badge">
            Stale
          </Badge>
        ) : null}
      </div>
      <p className="text-xs text-zinc-600 dark:text-zinc-400">
        <span className="capitalize">{account.stage}</span>
        {account.stageNote ? (
          <>
            {' · '}
            <InlineMarkdown text={account.stageNote} />
          </>
        ) : null}{' '}
        · <span className="capitalize">{account.segment}</span>
        {primaryContact ? ` · ${primaryContact.name}` : ''}
      </p>
      {shownAction ? <p className="whitespace-pre-wrap break-words">{shownAction}</p> : null}
      <p className="text-xs text-zinc-500">
        Last touch {account.lastTouch ?? '—'} · next {item.nextDate ?? '—'}
      </p>
      <button type="button" aria-expanded={editing} onClick={() => setEditing(!editing)} className={LINK_BUTTON}>
        {editing ? 'Done editing' : 'Edit'}
      </button>
      {editing ? (
        <div data-testid="crm-row-editors" className="pt-1">
          <AccountEditors account={account} />
        </div>
      ) : null}
    </li>
  );
}
